// Which template renders this tab (ARCHITECTURE §5.1). First match wins:
//   1. the admin preview frame (the draft's site_template, merged into the
//      tenant by PreviewBridge's usePreviewTenant);
//   2. the dev override (?__template=, dev/staging builds only);
//   3. the published choice (tenant.site_template, incl. the cached tenant);
//   4. classic.
// An unknown or unregistered id falls back to classic with one console.warn.
// Options: defaults filled, unknown/invalid keys dropped (resolveOptions).

import type { TenantInfo } from '@/contexts/LeagueContext'
import { DEFAULT_SITE_TEMPLATE_ID, getManifest } from './registry'
import { resolveOptions, type RawOptions } from './options'
import type { TemplateOverride } from './devOverride'
import type { SiteTemplateManifest } from './types'

export interface ResolvedTemplate {
  manifest: SiteTemplateManifest
  options: RawOptions
  source: 'preview' | 'override' | 'published' | 'default'
}

const warned = new Set<string>()
function warnOnce(id: string, source: string) {
  if (warned.has(id)) return
  warned.add(id)
  console.warn(`[site-templates] unknown website template "${id}" (${source}); using classic`)
}

export function resolveTemplate(input: {
  tenant: Pick<TenantInfo, 'site_template'> | null
  inPreview: boolean
  override: TemplateOverride | null
}): ResolvedTemplate {
  const published = input.tenant?.site_template ?? null
  const candidates: Array<{ id: string; options?: RawOptions; source: ResolvedTemplate['source'] }> = []
  if (input.inPreview && published) candidates.push({ ...published, source: 'preview' })
  if (input.override) candidates.push({ ...input.override, source: 'override' })
  if (!input.inPreview && published) candidates.push({ ...published, source: 'published' })
  for (const c of candidates) {
    const manifest = getManifest(c.id)
    if (manifest) return { manifest, options: resolveOptions(manifest.options, c.options ?? {}), source: c.source }
    warnOnce(c.id, c.source)
  }
  const classic = getManifest(DEFAULT_SITE_TEMPLATE_ID)
  if (!classic) throw new Error('site templates: the classic manifest is not registered')
  return { manifest: classic, options: resolveOptions(classic.options, {}), source: 'default' }
}
